"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Heart, ShieldCheck } from "lucide-react";
const experts = [{
  name: "Dr. Alistair Vance",
  position: "Chief Economist",
  bio: "Former Treasury advisor with 20 years of experience in macroeconomic modeling and national policy.",
  favoriteZone: "Housing Pressure Index",
  certification: "PhD Economics, ANU",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/alistair_vance_economist.png"
}, {
  name: "Marcus Thorne",
  position: "Head of Data Science",
  bio: "Specializes in high-frequency data ingestion and algorithmic validation for household indicators.",
  favoriteZone: "Cost of Living Exposure Index",
  certification: "MSc Applied Statistics",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/marcus_thorne_data.png"
}, {
  name: "Elena Rossi",
  position: "Director of Research",
  bio: "Leads the AIB methodology framework, ensuring independent and media-ready statistical reporting.",
  favoriteZone: "Financial Wellness Index",
  certification: "Senior Research Fellow",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/elena_rossi_research.png"
}, {
  name: "Julian Chen",
  position: "Policy Analyst",
  bio: "Focuses on the intersection of rental exposure and urban development across Australian capitals.",
  favoriteZone: "Rental Exposure Index",
  certification: "Public Policy Specialist",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/julian_chen_analyst.png"
}];
export default function MeetThePlayExperts() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223765" id="the-leadership-team" className="bg-background py-24 overflow-hidden text-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-5xl font-default font-bold text-primary mb-6 tracking-tight">
            The Leadership Team
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Our executive board combines decades of economic expertise with a 
            commitment to providing independent, data-driven insights for all Australians.
          </p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {experts.map((expert, i) => <div data-index={i} key={i} className="flex flex-col items-center">
              {}
              <div className="group relative aspect-square w-full max-w-[280px] mb-6 [perspective:1000px]">
                <div className="relative h-full w-full transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)] shadow-sm border border-border">
                  
                  {}
                  <div className="absolute inset-0 h-full w-full [backface-visibility:hidden]">
                    <SafeHydrate><Avatar className="size-full border-0">
                      <AvatarImage src={expert.image} alt={expert.name} className="object-cover grayscale hover:grayscale-0 transition-all duration-500" />
                      <AvatarFallback className="bg-muted text-muted-foreground text-4xl font-default font-bold">
                        {expert.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                  </div>

                  {}
                  <div className="absolute inset-0 h-full w-full bg-primary p-6 text-primary-foreground [backface-visibility:hidden] [transform:rotateY(180deg)] flex flex-col justify-center items-center text-center">
                    <p className="text-sm md:text-base font-default font-medium mb-6 leading-relaxed">
                      "{expert.bio}"
                    </p>
                    <div className="mt-auto flex flex-col items-center gap-2">
                      <div className="flex items-center gap-1.5 text-primary-foreground/70">
                        <Heart className="size-4" />
                        <span className="text-xs uppercase tracking-widest font-bold font-mono">
                          Primary Focus
                        </span>
                      </div>
                      <span className="font-mono font-semibold text-sm">
                        {expert.favoriteZone}
                      </span>
                    </div>
                  </div>

                </div>
              </div>

              {}
              <div className="text-center flex flex-col items-center">
                <h3 className="text-xl font-default font-bold text-foreground mb-1 tracking-tight">
                  {expert.name}
                </h3>
                <p className="text-muted-foreground font-medium mb-4 font-default">
                  {expert.position}
                </p>
                <Badge variant="secondary" className="bg-secondary text-secondary-foreground hover:bg-muted border-border px-3 py-1">
                  <ShieldCheck className="size-3.5 mr-1.5" />
                  <span className="font-mono text-[10px] uppercase tracking-wider">{expert.certification}</span>
                </Badge>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}